import { Checkout } from '../../models/Checkout';
import { OPTION_TYPES } from '../../models/ShippingOption';
import { CheckoutSerializer } from '../serializers/CheckoutSerializer';
import { ApiHttpService, ApiHttpServiceNewApi } from '../ApiHttpService';
import { ParamsHelper } from '../../helpers/ParamsHelper';
import { ApiError } from '../../models/ApiError';

const CHECKOUT_CONFIRM_PATH = 'usuario/checkout';
const CHECKOUT_PATH = 'usuario/getcheckout';
const CHECKOUTTRANSACTION_PATH = 'usuario/getcheckoutbytransaction';
const CHECKOUTS_PATH = 'usuario/checkouts';
const CANCEL_CHECKOUT_PATH = 'usuario/CancelCheckout';
const ATTEMP_PAY_PATH = 'usuario/CheckoutNewTransaction';
const BILLING_PDF = 'usuario/GetPDFComprobante';

const NEWAPI_CHECKOUTTRANSACTION_PATH = 'checkout/search-transaction-status';

class CheckoutController {
  static serializeShipping(params) {
    if (params.shippingInfo.optionType === OPTION_TYPES.SIMPLE) {
      return {
        Fecha: params.shippingInfo.date,
        HoraInicio: params.shippingInfo.startHour,
        HoraFin: params.shippingInfo.endHour,
      };
    }
    return {
      Fecha: params.timeSlot.date,
      HoraInicio: params.timeSlot.startHour,
      HoraFin: params.timeSlot.endHour,
    };
  }

  static serializeBank(params) {
    if (!params.bank) {
      return null;
    }
    return {
      ID: params.bank.id,
      Nombre: params.bank.name,
    };
  }

  static serializeInstallations(params) {
    if (!params.installationOptions) {
      return null;
    }
    return params.installationOptions.map((entry) => {
      const serializedInfo = entry.selectedOptions.map(item => ({
        Cantidad: item.amount,
        Viatico: item.extraCost,
        Fecha: item.date.toISO(),
        HoraF: item.endHour,
        HoraI: item.startHour,
      }));
      return {
        IDArticulo: entry.productId,
        Instalaciones: serializedInfo,
      };
    });
  }

  static serializeCart(params) {
    const serializedCart = [];
    params.cart.forEach((item) => {
      // Calculate the amount of rejected installations
      let rejectedInstallations = 0;
      if (item.serviceAmount) {
        let selectedInstallations = 0;
        (params.installationOptions || []).forEach((entry) => {
          if (entry.productId === item.product.id) {
            selectedInstallations += entry.selectedOptions.reduce((acc, elem) => (
              acc + elem.amount
            ), 0);
          }
        });
        rejectedInstallations = item.serviceAmount - selectedInstallations;
      }
      serializedCart.push({
        Articulo: {
          ArtId: item.product.id,
        },
        Cantidad: item.amount,
        InstalacionesSinCoordinar: rejectedInstallations,
      });
      if (item.serviceAmount) {
        const { serviceProduct } = item.product;
        let serviceEntry = serializedCart.find(elem => elem.Articulo.ArtId === serviceProduct.id);
        if (!serviceEntry) {
          serviceEntry = {
            Articulo: {
              ArtId: serviceProduct.id,
            },
            Cantidad: 0,
          };
          serializedCart.push(serviceEntry);
        }
        serviceEntry.Cantidad += item.serviceAmount;
      }
    });
    return serializedCart;
  }

  static serializeCheckout(params) {
    return {
      ClienteWeb: params.userId,
      FormaDePago: params.paymentType.id,
      Banco: CheckoutController.serializeBank(params),
      Total: params.totalPrice,
      DatosFacturacion: {
        DFWId: params.billingDataId,
      },
      Comentario: params.additionalNotes,
      Articulos: CheckoutController.serializeCart(params),
      Instalaciones: CheckoutController.serializeInstallations(params),
      // NOTE: Since the backend supports multiple shipments, we need
      // to send a one-item list.
      Envios: !params.shippingInfo ? null : [{
        Agencia: params.agency ? params.agency.id : null,
        Direccion: params.addressId,
        FechaPrometida: CheckoutController.serializeShipping(params),
        IdTipoFlete: params.shippingInfo.shippingType,
        Telefono1: !params.firstPhone ? null : {
          ID: params.firstPhone.id,
        },
        Telefono2: !params.secondPhone ? null : {
          ID: params.secondPhone.id,
        },
        ValorFlete: params.shippingInfo.price,
        ValorPiso: params.agency ? params.agency.extraCost : null,
      }],
    };
  }

  static async create(params) {
    const authenticated = true;
    const serializedParams = CheckoutController.serializeCheckout(params);
    const response = await ApiHttpService.post(
      CHECKOUT_CONFIRM_PATH,
      serializedParams,
      authenticated,
    );
    const serializedCheckout = CheckoutSerializer.deSerialize(response.data);
    return new Checkout(serializedCheckout);
  }

  static async attempPay(checkoutId, bankId) {
    const authenticated = true;
    const response = await ApiHttpService.post(
      ATTEMP_PAY_PATH,
      {
        IdBanco: bankId,
        IdPedido: checkoutId,
      },
      authenticated,
    );
    const serializedCheckout = CheckoutSerializer.deSerialize(response.data);
    return new Checkout(serializedCheckout);
  }


  static async find(id) {
    const authenticated = true;
    const response = await ApiHttpService.get(CHECKOUT_PATH, { pedido: id }, authenticated);
    const deSerializedData = CheckoutSerializer.deSerialize(response.data);
    return new Checkout(deSerializedData);
  }

  static async findByTransaction(id) {
    const authenticated = true;
    const response = await ApiHttpService
      .get(`${CHECKOUTTRANSACTION_PATH}/${id}`, {}, authenticated);
    if (!response.data) {
      throw new ApiError({
        message: 'La transacción que intenta visualizar no existe',
        status: 404,
        code: 404,
      });
    }
    const deSerializedData = CheckoutSerializer.deSerialize(response.data);
    return new Checkout(deSerializedData);
  }

  static async findStatusByTransaction(id) {
    const authenticated = true;
    const response = await ApiHttpServiceNewApi
      .get(`${NEWAPI_CHECKOUTTRANSACTION_PATH}/${id}`, {}, authenticated);
    return response.data.data;
  }

  static async findBillingPDF(id) {
    const authenticated = true;
    const response = await ApiHttpService
      .get(`${BILLING_PDF}?token=${id}`, {}, authenticated);
    if (!response.data) {
      throw new ApiError({
        message: 'La factura que intenta visualizar no existe',
        status: 404,
        code: 404,
      });
    }
    return CheckoutSerializer.deSerializePDF(response.data);
  }

  static async all() {
    const authenticated = true;
    const response = await ApiHttpService.get(CHECKOUTS_PATH, {}, authenticated);
    const deSerializedData = (response.data || []).map(CheckoutSerializer.deSerialize);
    return deSerializedData.map(data => new Checkout(data));
  }

  static async cancel(checkoutId) {
    const authenticated = true;
    const params = { pedido: checkoutId };
    const cancelCheckoutPath = `${CANCEL_CHECKOUT_PATH}${ParamsHelper.createQueryParams(params)}`;
    const response = await ApiHttpService.post(cancelCheckoutPath, {}, authenticated);
    const deSerializeData = CheckoutSerializer.deSerialize(response.data);
    return new Checkout(deSerializeData);
  }
}

export { CheckoutController };



// WEBPACK FOOTER //
// ./src/networking/controllers/CheckoutController.js